<template>
  <div class="map-overlay">
    <div class="content">
      <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
      <div
        class="img"
        tabindex="-1"
        data-ident="mapObjectOverlayImage"
        @click="closeMap('Image')"
      >
        <watch-btn
          v-if="objectNumber"
          :object-number="objectNumber"
          :price-object="priceObject"
          :min-price="searchObject.minPrice"
          :extra-class="'map-overlay-watch-btn'"
        />
        <gallery-slider-custom
          v-if="objectNumber && objectData.images.length"
          :key="objectNumber + '_slider'"
          :images-arr="sliderCustomImages"
          :wrapper="'gallery gallery-object gallery-map'"
          :share="false"
          :show-controls="false"
          :gallery-link="objectRouterLink"
          :image-type="'mapObjectOverlay'"
          :object-number="objectNumber"
        />
      </div>
      <div class="text">
        <div class="padding">
          <!-- eslint-disable-next-line vuejs-accessibility/heading-has-content -->
          <h4
            class="title"
            :aria-label="$t('object:objectDetails')"
            tabindex="0"
            data-ident="mapObjectOverlayTitle"
            @click="closeMap('Title')"
            @keypress="closeMap('Title')"
          >
            <router-link-simple
              v-dompurify-html="objectData.title"
              :to="objectRouterLink"
            />
          </h4>
          <div class="row row-tight">
            <div class="col col-sm-7 col-lg-12 col-xl-7">
              <small>{{ keyData }}</small>
              <object-ratings-aggregation
                v-if="objectData.ratingCount > 0"
                :total="objectData.ratingCount"
                :average="objectData.ratingAverage"
                :average-of-total="{written:false,hidden:false}"
                style="min-width: 9em;"
              />
            </div>
            <div class="col-12 col-sm-5 col-lg-12 col-xl-5 text-right">
              <object-price-original
                :show-off="true"
                :price="objectData.price"
                :currency="priceObject.currency"
                :no-decimals="true"
                :price-percent-reduction="priceObject.pricePercentReduction"
              />
              <object-price-duration :duration="priceObject.duration" />
              <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
              <span
                data-ident="mapObjectOverlayDeails"
                @click="closeMap('Details')"
              >
                <router-link-simple
                  tabindex="-1"
                  :to="objectRouterLink"
                  class="btn btn-primary btn-sm d-none d-sm-inline-block d-lg-none d-xl-inline-block"
                >
                  {{ $t('common:objectDetailsBtn') }}
                </router-link-simple>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import GallerySliderCustom from 'components/GallerySliderCustom.vue';
import RouterLinkSimple from 'components/RouterLinkSimple.vue';
import ObjectRatingsAggregation from 'components/ObjectRatingsAggregation.vue';
import ObjectPriceOriginal from 'components/ObjectPriceOriginal.vue';
import ObjectPriceDuration from 'components/ObjectPriceDuration.vue';
import WatchBtn from 'components/WatchBtn.vue';
import { getClearedFilter } from 'utils/SearchFilterHelper';
// import IconChevronRightAlternative from 'ICON:IconChevronRightAlternative';
import DeviceHelper from 'mixins/DeviceHelper';
import { mapMutations } from 'vuex';
import GtmTools from 'mixins/GtmTools';

export default {
  name: 'MapObjectOverlay',
  components: {
    GallerySliderCustom,
    ObjectRatingsAggregation,
    ObjectPriceOriginal,
    ObjectPriceDuration,
    WatchBtn,
    RouterLinkSimple,
    // IconChevronRightAlternative,
  },
  mixins: [
    DeviceHelper,
    GtmTools,
  ],
  props: {
    objectNumber: {
      type: [Number, String],
      default: 0,
    },
    // needed for watchlist, search results are separated for search and watchlist
    searchObjectsSource: {
      type: String,
      required: false,
      default: 'searchObjects',
    },
  },
  data() {
    return {
      objectData: {
        images: [],
        title: '',
      },
      breakpointPrefix: '.short',
    };
  },
  computed: {
    keyData() {
      const { keyData } = this.objectData;
      return keyData;
    },
    sliderCustomImages() {
      return this.objectData.images.slice(0, 1);
    },
    searchObject() {
      switch (this.searchObjectsSource) {
        case 'searchObjects':
          return this.$store.getters.getSearchObjectById(this.objectNumber);
        case 'watchedObjectsSearch':
          return this.$store.getters.getWatchedObjectById(this.objectNumber);
        default:
          return this.$store.getters.getSearchObjectById(this.objectNumber);
      }
    },
    // builds the price object needed for price display from the search object data
    priceObject() { // copied from searchObject
      return {
        currency: 'EUR',
        original: 0,
        price: ((this.searchObject.price) ? this.searchObject.price : null),
        startDate: ((typeof this.searchObject.startDate === 'undefined') ? null : this.searchObject.startDate),
        endDate: ((typeof this.searchObject.endDate === 'undefined') ? null : this.searchObject.endDate),
        duration: this.searchObject.duration || 7,
        persons: ((typeof this.searchObject.persons === 'undefined') ? null : this.searchObject.persons),
      };
    },
    // calculates a parameter needed to build the translation key
    langWeekDays() { // copied from searchObject
      return this.priceObject.duration % 7 === 0 ? 'Week' : 'Days';
    },
    // returns search filter url query for object link (returntype is object {})
    searchFilterObjectQuery() {
      // create new object to leave this.priceObject untouched
      let searchFilter = JSON.parse(JSON.stringify(this.priceObject));
      searchFilter = getClearedFilter(searchFilter, [], ['startDate', 'endDate', 'persons']);
      return searchFilter;
    },
    objectRouterLink() {
      const routerObject = {
        path: this.searchObject.seoLinkObject,
        query: {
          ...this.searchFilterObjectQuery,
        },
      };
      return routerObject;
    },
  },
  watch: {
    objectNumber: {
      handler: 'objectChanged',
      immediate: true,
    },
    dh_breakpoint() {
      if (['xs', 'sm'].includes(this.dh_breakpoint)) {
        this.breakpointPrefix = '.short';
      } else {
        this.breakpointPrefix = '';
      }
    },
  },
  methods: {
    ...mapMutations({ setMapStatus: 'SET_MAP_STATUS' }),
    closeMap(eventTarget) {
      this.setMapStatus({ fullscreen: false });
      this.dataLayerPush({ event: 'mapInteractionPush', mapInteractionType: 'ObjectCTA', target: eventTarget });
    },
    objectChanged() {
      if (!this.objectNumber) { return; }
      const stateObjectData = this.searchObject;
      if (stateObjectData) {
        const keyProps = ['personsMax', 'countRoomsSleeping', 'squaremeterLiving'];
        const availableProps = [];
        keyProps.forEach((prop) => {
          if (stateObjectData[prop]) {
            availableProps.push(this.$i18n.i18next.translator.translate(`common:keyData${this.breakpointPrefix}.${prop}`, { count: stateObjectData[prop] }));
          }
        });

        this.objectData = {
          images: stateObjectData.objectImages || [{ src: stateObjectData.objectImages }],
          title: stateObjectData.title,
          squaremeterLiving: stateObjectData.squaremeterLiving,
          ratingAverage: stateObjectData.ratingAverage,
          ratingCount: stateObjectData.ratingCount,
          location: stateObjectData.seoLinkLocationName,
          locationHref: stateObjectData.seoLinkLocation,
          price: stateObjectData.price || null,
          keyData: availableProps.join(' · '),
        };
      }
    },
  },
};
</script>
<style lang="scss" scoped>

.map-overlay {
  position: absolute;
  width: 100%;
  height: auto;
  margin: 0 auto;
  z-index: 1005;
  bottom: 0;
  text-align: center;
  padding: 0 $margin_content_xs;

  &.hidden {
    display: none;
  }

  &.visible {
    display: block;
  }

  .rating-stars {
    margin-top: 0.5em;
  }

  .padding {
    padding: $half_margin_content $margin_content_xs $margin_content_xs;
  }

  /deep/ .gallery {
    min-height: 0;
  }

  /* https://www.andyshora.com/css-image-container-padding-hack.html Padding Bottom = (Image Height / Image Width) * 100% */
  .img {
    position: relative;
    display: block;

    .map-overlay-watch-btn {
      position: absolute;
      background: none;

      @include font_size_l;

      right: 0;
      top: 0;
      z-index: 5;
      border: none;
      padding: 0.3em;
      line-height: 1em;

      &:focus {
        border-color: inherit;
        box-shadow: none;
      }
    }

    img {
      width: 100%;
      position: absolute;
    }
  }

  .btn-icon-only {
    .icon {
      margin: 0;
    }
  }
}

.content {
  padding: 0;
  margin: $margin_content_xs auto;
  background: #fff;
  overflow: hidden;
  text-align: left;

  @include radius($radius_card);

  display: flex;
  flex-direction: row;
  align-content: stretch;
  align-items: stretch;

  /deep/ .img {
    padding-bottom: 0;
    height: auto;
    width: 20%;
    min-width: 20%;

    a {
      display: block;
      background: $color_grey_bg;
    }
  }

  @media #{$media_xxl} {
    max-width: 30em;

    .img {
      width: 27%;
    }

    .text {
      width: 73%;
    }
  }

  @media #{$media_xl} {
    max-width: 30em;

    .img {
      width: 30%;
    }

    .text {
      width: 70%;
    }
  }

  @media #{$media_lg} {
    max-width: 22em;

    .img {
      width: 40%;
    }

    .text {
      width: 60%;
    }
  }

  @media #{$media_md} {
    max-width: 28em;

    .img {
      width: 30%;
    }

    .text {
      width: 70%;
    }
  }

  @media #{$media_sm} {
    width: $responsive_xs_max - $margin_content*2;

    .img {
      width: 27%;
    }

    .text {
      width: 73%;
    }
  }

  @media #{$media_xs} {
    width: 100%;

    .img {
      width: 41%;
    }

    .text {
      width: 59%;
    }
  }

  &,
  #app:not(.isTouchDevice) &:hover {
    color: var(--color_text);
  }

  h4,
  h5 {
    margin: 0;
  }

  /deep/ .vacancy-period {
    @include font_size_s;

    line-height: 1em;
    margin: 0;
  }

  .title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-block;
    width: 100%;

    @include font_size_m;
    @include font_bold;

    margin: 0;
  }

  /deep/ small {
    display: block;
    line-height: 1.2em;
    margin: 0.25em 0 0.5em;
  }

  .price {
    margin: 0;
  }

  /deep/ .rating-stars {
    margin-top: 0.15em;

    em {
      @include font_size_s;
    }
  }
}

/deep/ .gallery {
  .counter {
    display: none;
  }

  .options {
    position: absolute;
    right: 0.25em;
    top: 0.25em;
    z-index: 5;

    @include font_size_xl;
  }

  .prev,
  .next {
    display: none;
  }
}

.btn.btn-primary {
  margin-top: 0.75em;
  padding: 0.2em 1.5em;

  @media #{$media_xs} {
    display: none;
  }
}
</style>
